import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useSound } from '../../utils/sound/SoundProvider';
import useGameBase from '../../hooks/useGameBase';

function FourInARowGame({ gameState, gamePlayers, user, onGameAction, isHost, onPlayAgain, socket, roomId }) {
  const width = gameState.width || 7;
  const height = gameState.height || 6;
  const board = gameState.board || Array((width || 7) * (height || 6)).fill(null);
  const [animDrop, setAnimDrop] = useState(null); // { id, column, row, color, stage, topStart, topTarget, local }
  const boardRef = useRef(null);
  const gridRef = useRef(null);
  const [cellSize, setCellSize] = useState(44);
  const [gridGap, setGridGap] = useState(6);
  const [gridPadding, setGridPadding] = useState(6);
  const sound = useSound();

  // Utilities: very small color helpers to create subtle gradients
  const sanitizeHex = (hex) => (typeof hex === 'string' ? hex.trim() : '');
  const adjustColor = (hex, percent) => {
    if (!hex || typeof hex !== 'string') return hex;
    const h = hex.trim();
    if (h[0] !== '#') return hex; // leave named colors untouched
    let v = h.slice(1);
    if (v.length === 3) v = v.split('').map(c => c + c).join('');
    if (v.length !== 6) return hex;
    const num = parseInt(v, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    const amt = Math.round(255 * (percent / 100));
    r = Math.max(0, Math.min(255, r + amt));
    g = Math.max(0, Math.min(255, g + amt));
    b = Math.max(0, Math.min(255, b + amt));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  // measure grid cell size/gap/padding so animations line up with CSS
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    // infer computed styles from a cell if present
  const firstCell = grid.querySelector('.fourinrow-cell');
    const cs = window.getComputedStyle(grid);
    const gap = parseFloat(cs.columnGap || cs.gridColumnGap || '8') || 8;
    const padding = parseFloat(cs.padding || '8') || 8;
    if (firstCell) {
  const rect = firstCell.getBoundingClientRect();
      setCellSize(Math.round(rect.width));
    }
    setGridGap(gap);
    setGridPadding(padding);
    // re-measure on resize
    const ro = new ResizeObserver(() => {
      const first = grid.querySelector('.fourinrow-cell');
      if (first) setCellSize(Math.round(first.getBoundingClientRect().width));
    });
  ro.observe(grid);
    window.addEventListener('resize', () => {
      const first = grid.querySelector('.fourinrow-cell');
      if (first) setCellSize(Math.round(first.getBoundingClientRect().width));
    });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', () => {});
    };
  }, []);

  const getCellColor = (symbol) => {
    if (!symbol) return null;
    if (symbol === 'A') return (gameState.colors && gameState.colors.A) || 'red';
    return (gameState.colors && gameState.colors.B) || 'yellow';
  };

  const { findPlayerById, isLocalPlayerTurn, submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const isMyTurn = isLocalPlayerTurn();
  const currentPlayerObj = findPlayerById(gameState?.currentPlayer);

  const handleColumnClick = (col) => {
    if (!isMyTurn || gameState.finished) return;
    const targetRow = getDropRow(col);
    if (targetRow === null) return; // column full
  const symbol = currentPlayerObj ? (currentPlayerObj.socketId === gamePlayers[0]?.socketId ? 'A' : 'B') : 'A';
  const color = getCellColor(symbol);
    const id = Date.now();
    // compute positions using measured sizes
    const topStart = gridPadding * 1 - (cellSize * 0.7); // start above board
  const topTarget = gridPadding + targetRow * (cellSize + gridGap) + (cellSize - DISC_SIZE) / 2;
  // hide any hover preview immediately so we don't show the next player's preview
  setHoverPreview({ visible: false, left: 0, column: null });
  setAnimDrop({ id, column: col, row: targetRow, color, stage: 'start', topStart, topTarget, local: true, placerSocketId: user?.socketId });
    // trigger fall on next frame to allow CSS transition
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setAnimDrop(a => a && a.id === id ? { ...a, stage: 'falling' } : a);
    }));
  // server-side authoritative action via standardized submitAction
  submitAction({ type: 'place', column: col }).catch(() => {});
  };

  const getDropRow = (col) => {
    for (let r = height - 1; r >= 0; r--) {
      const idx = r * width + col;
      if (!board[idx]) return r;
    }
    return null;
  };

  // Find winning cells for highlight (returns array of indices)
  const findWinningCells = (symbol) => {
    if (!symbol) return [];
    const dirs = [ [1,0], [0,1], [1,1], [1,-1] ];
    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        const idx0 = r * width + c;
        if (board[idx0] !== symbol) continue;
        for (const [dc, dr] of dirs) {
          const cells = [idx0];
          for (let s = 1; s < 4; s++) {
            const nc = c + dc * s;
            const nr = r + dr * s;
            if (nc < 0 || nc >= width || nr < 0 || nr >= height) break;
            const idx = nr * width + nc;
            if (board[idx] === symbol) cells.push(idx); else break;
          }
          if (cells.length >= 4) return cells;
        }
      }
    }
    return [];
  };

  const winningCells = findWinningCells(gameState.winnerSymbol || null);

  // layout measurements (should match the CSS used below)
  const CELL_SIZE = cellSize; // measured cell size
  const GAP = gridGap; // measured gap
  const BOARD_PADDING = gridPadding; // measured padding

  const HOLE_SIZE = Math.round(CELL_SIZE * 0.86);
  // Keep placed discs, hover preview, and falling animation in sync.
  const DISC_SIZE = Math.max(30, Math.min(Math.round(HOLE_SIZE * 1.06), CELL_SIZE - 4));

  // animation timing constants (ms) - keep transitions and timeouts in sync
  const FALL_DURATION_MS = 320;
  const BOUNCE_DELAY_MS = 0; // small gap after fall finishes
  const BOUNCE_UP_MS = 70;
  const BOUNCE_DOWN_MS = 120;
  // bounce amplitude (percentage of cell size) and minimum pixels
  const BOUNCE_AMPLITUDE = 0.22; // 12% of cell height
  const MIN_BOUNCE_PX = 8;

  // keep previous board to detect remote placements and animate them
  const prevBoardRef = useRef(board);
  // timeouts used to sequence the small bounce after landing
  const bounceTimeoutsRef = useRef([]);
  // ensure win sound is played only once per winning animation
  const playedWinRef = useRef(false);

  // clear bounce timeouts on unmount
  useEffect(() => {
    return () => {
      bounceTimeoutsRef.current.forEach(t => clearTimeout(t));
      bounceTimeoutsRef.current = [];
    };
  }, []);

  // when the board changes (server update), detect newly placed disc(s) and start a non-local animation
  useEffect(() => {
    const prev = prevBoardRef.current || [];
    // if lengths differ or no previous, just update and skip
    if (!prev || prev.length !== board.length) {
      prevBoardRef.current = board;
      return;
    }
  for (let i = 0; i < board.length; i++) {
      if (prev[i] !== board[i]) {
        // found a change
        if (!prev[i] && board[i]) {
          // a new disc was placed at index i
          const row = Math.floor(i / width);
          const col = i % width;
          // don't double-animate if we already have an animDrop for this cell
      if (animDrop && animDrop.column === col && animDrop.row === row) break;
      const color = board[i] ? (board[i] === 'A' ? (gameState.colors?.A || 'red') : (gameState.colors?.B || 'yellow')) : null;
          const id = Date.now() + Math.floor(Math.random() * 1000);
          const topStart = BOARD_PADDING * 1 - (cellSize * 0.7);
          const topTarget = BOARD_PADDING + row * (CELL_SIZE + GAP) + (CELL_SIZE - DISC_SIZE) / 2;
          setHoverPreview({ visible: false, left: 0, column: null });
          setAnimDrop({ id, column: col, row, color, stage: 'start', topStart, topTarget, local: false, placerSocketId: null });
          // trigger fall on next frame
          requestAnimationFrame(() => requestAnimationFrame(() => {
            setAnimDrop(a => a && a.id === id ? { ...a, stage: 'falling' } : a);
            try { sound && sound.play && sound.play('fourinrow:drop'); } catch (e) {}
          }));
          break; // only animate the first detected placement
        }
      }
    }
    prevBoardRef.current = board;
  }, [board, CELL_SIZE, GAP, BOARD_PADDING, animDrop, cellSize, width, gameState.colors, DISC_SIZE, sound]);

  // play win sound when showing winning state
  useEffect(() => {
    try {
      if (gameState.showWinningState && winningCells && winningCells.length && !playedWinRef.current) {
        playedWinRef.current = true;
        sound && sound.play && sound.play('ui:positive');
      }
      // reset the flag when the winning state clears so future wins will play again
      if (!gameState.showWinningState) {
        playedWinRef.current = false;
      }
    } catch (e) {}
  }, [gameState.showWinningState, winningCells, sound]);

  // no hover preview by request
  const [hoverPreview, setHoverPreview] = useState({ visible: false, left: 0, column: null });

  return (
    <div className="mx-auto mt-4 flex w-full max-w-4xl flex-col gap-4 px-2">
  {/* winning disc highlight: animate background color to green and back when appropriate */}
      <style>{`
        @keyframes winColorCycle { 
          0% { fill: var(--disc-color); }
          50% { fill: #16a34a; }
          100% { fill: var(--disc-color); }
        }
        .fourinrow-pulse svg circle { animation: winColorCycle 900ms ease-in-out infinite; }
      `}</style>
      {/* Current turn indicator */}
      {/* <div className="flex items-center gap-3">
        {gameState.finished ? (
          <div className="text-sm font-semibold">{gameState.winnerSymbol === 'draw' ? 'Round ended: Draw' : 'Round ended'}</div>
        ) : (
          (() => {
            const cur = gamePlayers.find(p => p.socketId === gameState.currentPlayer || p.userId === gameState.currentPlayer);
            return (
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600 dark:text-gray-400">Current turn:</div>
                <div className="flex items-center gap-2 font-semibold">
                  <span style={{ width: 14, height: 14, borderRadius: 4, backgroundColor: cur ? (cur.socketId === gamePlayers[0]?.socketId ? (gameState.colors?.A || 'red') : (gameState.colors?.B || 'yellow')) : '#888' }} />
                  <span className="text-gray-600 dark:text-gray-400">{cur ? cur.username : 'Waiting...'}</span>
                </div>
              </div>
            );
          })()
        )}
      </div> */}

  {/* Hover anywhere on the board to preview a column and click anywhere in that column to drop (no arrows) */}

      {/* Board */}
      <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-stone-100 via-amber-50 to-orange-100 p-2 shadow-sm dark:border-amber-900/70 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 sm:p-3">
      <div className="relative mx-auto mt-10 w-full max-w-[min(92vw,44rem)]" style={{ aspectRatio: `${width} / ${height}` }} ref={boardRef}
        onMouseMove={(e) => {
          if (!isMyTurn || gameState.finished || animDrop) return;
          const boardEl = boardRef.current;
          if (!boardEl) return;
          const rect = boardEl.getBoundingClientRect();
          const x = e.clientX - rect.left; // position inside boardRef
          const rel = x - BOARD_PADDING;
          const colWidth = CELL_SIZE + GAP;
          let col = Math.floor(rel / colWidth);
          if (col < 0) col = 0;
          if (col >= width) col = width - 1;
          const left = BOARD_PADDING + col * colWidth + (CELL_SIZE - DISC_SIZE) / 2;
          setHoverPreview({ visible: true, left, column: col });
        }}
        onMouseLeave={() => setHoverPreview({ visible: false, left: 0, column: null })}
        onClick={(e) => {
          if (!isMyTurn || gameState.finished || animDrop) return;
          const boardEl = boardRef.current;
          if (!boardEl) return;
          const rect = boardEl.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const rel = x - BOARD_PADDING;
          const colWidth = CELL_SIZE + GAP;
          let col = Math.floor(rel / colWidth);
          if (col < 0) col = 0;
          if (col >= width) col = width - 1;
          handleColumnClick(col);
        }}
      >
  <div ref={gridRef} className="h-full w-full rounded-2xl bg-gray-300 p-2 shadow-lg dark:bg-gray-800" style={{ display: 'grid', gridTemplateColumns: `repeat(${width}, minmax(0, 1fr))`, gap: GAP, padding: gridPadding }}>
          {Array.from({ length: height }).map((_, r) => (
            // render rows top-to-bottom
            Array.from({ length: width }).map((__, c) => {
              const idx = r * width + c;
              const symbol = board[idx];
  // hide the final disc in the cell while a local falling animation is running into it
  // as soon as a local animDrop is scheduled we hide the target cell so the disc doesn't appear
  // at the bottom before the falling animation begins
  const isAnimatingIntoThis = animDrop && animDrop.column === c && animDrop.row === r;
  const discColor = (!isAnimatingIntoThis) ? getCellColor(symbol) : null;
  const discGradientId = discColor ? `disc-grad-${sanitizeHex(discColor).replace(/[^a-zA-Z0-9]/g, '')}` : null;
  const discInner = discColor ? adjustColor(discColor, 8) : discColor; // slightly lighter center
  const discOuter = discColor ? adjustColor(discColor, -6) : discColor; // slightly darker edge
              const isWinning = winningCells.includes(idx);
              return (
                <div key={`cell-${idx}`} className="fourinrow-cell relative flex aspect-square items-center justify-center" style={{ width: '100%', height: '100%' }}>
                  {/* hole */}
                  <div style={{ width: '100%', height: '100%' }} className={`rounded-full bg-gray-400 dark:bg-gray-800 flex items-center justify-center`}>
                    {/* inner circle (light in light mode, gray in dark mode) */}
                    <div style={{ width: HOLE_SIZE, height: HOLE_SIZE }} className="rounded-full bg-white dark:bg-gray-700 flex items-center justify-center">
                      {/* disc if present */}
                      {discColor ? (
                        <div className={(isWinning && gameState.showWinningState && !animDrop) ? 'fourinrow-pulse' : ''} style={{ width: DISC_SIZE, height: DISC_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', '--disc-color': discColor }}>
                          <svg width={DISC_SIZE} height={DISC_SIZE} viewBox={`0 0 ${DISC_SIZE} ${DISC_SIZE}`} aria-hidden>
                            <defs>
                              <radialGradient id={discGradientId} cx="35%" cy="25%" r="80%">
                                <stop offset="0%" stopColor={discInner} stopOpacity="1" />
                                <stop offset="60%" stopColor={discColor} stopOpacity="1" />
                                <stop offset="100%" stopColor={discOuter} stopOpacity="1" />
                              </radialGradient>
                            </defs>
                            <circle cx={DISC_SIZE / 2} cy={DISC_SIZE / 2} r={(DISC_SIZE - 4) / 2} fill={`url(#${discGradientId})`} stroke="rgba(0,0,0,0.22)" strokeWidth={2.5} />
                          </svg>
                        </div>
                      ) : (
                        <div style={{ width: DISC_SIZE, height: DISC_SIZE, borderRadius: '50%', backgroundColor: 'transparent' }} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ))}
        </div>
  {/* mouse-following preview disc (shows current player's disc color under mouse) */}
        {hoverPreview.visible && !animDrop && (
          (() => {
            const cur = currentPlayerObj;
            const symbol = cur ? (cur.socketId === gamePlayers[0]?.socketId ? 'A' : 'B') : 'A';
            const color = getCellColor(gameState.winnerSymbol === 'draw' ? null : symbol) || (gameState.colors?.A || 'red');
            const left = hoverPreview.left;
            const top = BOARD_PADDING - (CELL_SIZE * 0.65);
            const hoverGradId = `hover-grad-${String(color).replace(/[^a-zA-Z0-9]/g, '')}`;
            const hoverInner = adjustColor(color, 8);
            const hoverOuter = adjustColor(color, -6);
            return (
      <div style={{ position: 'absolute', left, top, width: DISC_SIZE, height: DISC_SIZE, pointerEvents: 'none', transition: 'left 60ms linear', zIndex: 5 }}>
      <div style={{ width: DISC_SIZE, height: DISC_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={DISC_SIZE} height={DISC_SIZE} viewBox={`0 0 ${DISC_SIZE} ${DISC_SIZE}`} aria-hidden>
          <defs>
            <radialGradient id={hoverGradId} cx="35%" cy="25%" r="80%">
              <stop offset="0%" stopColor={hoverInner} />
              <stop offset="60%" stopColor={color} />
              <stop offset="100%" stopColor={hoverOuter} />
            </radialGradient>
          </defs>
          <circle cx={DISC_SIZE / 2} cy={DISC_SIZE / 2} r={(DISC_SIZE - 4) / 2} fill={`url(#${hoverGradId})`} stroke="rgba(0,0,0,0.22)" strokeWidth={2.5} />
        </svg>
      </div>
        </div>
            );
          })()
        )}

            {/* Overlay frame: SVG mask so falling disc appears behind the frame with crisp circular holes */}
            <svg
              aria-hidden
              style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 10, width: `${CELL_SIZE * width + GAP * (width - 1) + BOARD_PADDING * 2}px`, height: `${CELL_SIZE * height + GAP * (height - 1) + BOARD_PADDING * 2}px` }}
              width={CELL_SIZE * width + GAP * (width - 1) + BOARD_PADDING * 2}
              height={CELL_SIZE * height + GAP * (height - 1) + BOARD_PADDING * 2}
              viewBox={`0 0 ${CELL_SIZE * width + GAP * (width - 1) + BOARD_PADDING * 2} ${CELL_SIZE * height + GAP * (height - 1) + BOARD_PADDING * 2}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <mask id="fourinrow-holes-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {Array.from({ length: height }).map((_, r) => (
                    Array.from({ length: width }).map((__, c) => {
                      const cx = BOARD_PADDING + c * (CELL_SIZE + GAP) + CELL_SIZE / 2;
                      const cy = BOARD_PADDING + r * (CELL_SIZE + GAP) + CELL_SIZE / 2;
                      const radius = Math.max(4, (CELL_SIZE - 6) / 2);
                      return <circle key={`hole-${r}-${c}`} cx={cx} cy={cy} r={radius} fill="black" />;
                    })
                  ))}
                </mask>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" className="fill-gray-300 dark:fill-gray-800" mask="url(#fourinrow-holes-mask)" />
            </svg>

        {/* Falling animation disc (cosmetic) */}
        {animDrop && (
          (() => {
            const { id, column, color, stage, topStart, topTarget, bouncePhase } = animDrop;
            const ANIM_SIZE = DISC_SIZE; // derive from DISC_SIZE so they match
            const left = BOARD_PADDING + column * (CELL_SIZE + GAP) + (CELL_SIZE - ANIM_SIZE) / 2;
            const delta = topTarget - topStart;
            // compute bounce amount based on cell size (higher bounce)
            const BOUNCE = Math.max(MIN_BOUNCE_PX, Math.round(CELL_SIZE * BOUNCE_AMPLITUDE));
            // determine translateY and transition based on stage / bouncePhase
            let translateY = 0;
            let transition = 'none';
            if (stage === 'start') {
              translateY = 0;
              transition = 'none';
            } else if (stage === 'falling') {
              translateY = delta;
              transition = `transform ${FALL_DURATION_MS}ms cubic-bezier(.2,.9,.2,1)`;
            } else if (stage === 'landed') {
              if (bouncePhase === 'up') {
                translateY = delta - BOUNCE;
                transition = `transform ${BOUNCE_UP_MS}ms cubic-bezier(.2,.9,.2,1)`;
              } else if (bouncePhase === 'down') {
                translateY = delta;
                transition = `transform ${BOUNCE_DOWN_MS}ms cubic-bezier(.2,.9,.2,1)`;
              } else {
                translateY = delta;
                transition = 'none';
              }
            }
            // keep constant disc size; we'll animate position via `top` (not transform)
            const handleTransitionEnd = () => {
              // when falling completes, start the small bounce up then down sequence
              if (stage === 'falling') {
                // clear any existing bounce timeouts
                bounceTimeoutsRef.current.forEach(t => clearTimeout(t));
                bounceTimeoutsRef.current = [];
                // mark landed
                setAnimDrop(a => a && a.id === id ? { ...a, stage: 'landed', bouncePhase: 0 } : a);
                // small delay then quick bounce up
                const t1 = setTimeout(() => {
                  setAnimDrop(a => a && a.id === id ? { ...a, bouncePhase: 'up' } : a);
                }, BOUNCE_DELAY_MS);
                // then quick bounce back down (delay = BOUNCE_DELAY + up duration)
                const t2 = setTimeout(() => {
                  setAnimDrop(a => a && a.id === id ? { ...a, bouncePhase: 'down' } : a);
                }, BOUNCE_DELAY_MS + BOUNCE_UP_MS);
                // finally remove the anim state (delay = BOUNCE_DELAY + up + down)
                const t3 = setTimeout(() => {
                  setAnimDrop(a => a && a.id === id ? null : a);
                }, BOUNCE_DELAY_MS + BOUNCE_UP_MS + BOUNCE_DOWN_MS);
                bounceTimeoutsRef.current.push(t1, t2, t3);
              }
            };
            // animate via transform translateY so the inner disc dimensions remain constant
            const transform = `translateY(${translateY}px)`;
            const transformTransition = transition;
            const animGradId = `anim-grad-${String(color).replace(/[^a-zA-Z0-9]/g, '')}`;
            const animInner = adjustColor(color, 8);
            const animOuter = adjustColor(color, -6);
            return (
              <div key={`anim-${id}`} style={{ position: 'absolute', left, top: topStart, width: ANIM_SIZE, height: ANIM_SIZE, pointerEvents: 'none', transform, transition: transformTransition, willChange: 'transform', zIndex: 5 }} onTransitionEnd={handleTransitionEnd}>
                <div style={{ width: ANIM_SIZE, height: ANIM_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width={ANIM_SIZE} height={ANIM_SIZE} viewBox={`0 0 ${ANIM_SIZE} ${ANIM_SIZE}`} aria-hidden>
                    <defs>
                      <radialGradient id={animGradId} cx="35%" cy="25%" r="80%">
                        <stop offset="0%" stopColor={animInner} />
                        <stop offset="60%" stopColor={color} />
                        <stop offset="100%" stopColor={animOuter} />
                      </radialGradient>
                    </defs>
                    <circle cx={ANIM_SIZE / 2} cy={ANIM_SIZE / 2} r={(ANIM_SIZE - 4) / 2} fill={`url(#${animGradId})`} stroke="rgba(0,0,0,0.22)" strokeWidth={2.5} />
                  </svg>
                </div>
              </div>
            );
          })()
        )}
        {/* Finished overlay */}
        {gameState.finished && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/30 rounded-lg w-full h-full" />
          </div>
        )}
      </div>
      </div>

      {/* Players legend */}
      <div className="grid gap-3 sm:grid-cols-2">
        {gamePlayers.slice(0, 2).map((p, idx) => {
          const key = p.userId || p.socketId || p.id || idx;
          const scoreKey = p.userId || p.socketId || p.id;
          const score = (normalizedState && normalizedState.scores && typeof normalizedState.scores[scoreKey] !== 'undefined') ? normalizedState.scores[scoreKey] : (p.score || 0);
          return (
          <div key={key} className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-3 shadow-sm dark:bg-gray-800">
            <div style={{  border: '1px solid black', width: 18, height: 18, borderRadius: 9999, backgroundColor: idx === 0 ? (gameState.colors?.A || 'red') : (gameState.colors?.B || 'yellow') }} />
            <div className="font-semibold text-gray-600 dark:text-gray-400">{p.username}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Score: {score}</div>
          </div>
        )})}
      </div>
    </div>
  );
}

FourInARowGame.Guide = function FourInARowGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to play Four in a Row</h3>
      <ul className="list-disc ml-6">
        <li>Players take turns dropping a disc into one of the columns.</li>
        <li>The disc occupies the lowest available cell in that column.</li>
        <li>First player to get four of their discs in a row (horizontal, vertical, diagonal) wins.</li>
      </ul>
    </div>
  );
};

export default FourInARowGame;
